/* ══════════════════════════════════════════════════════════════
   Market Breadth Visualization — AD Ratio, 52w, Delivery
   ══════════════════════════════════════════════════════════════ */

import { fetchBreadth } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

export interface BreadthData {
  advancers: number; decliners: number; unchanged: number;
  high52w: number; low52w: number;
  aboveEMA200: number | null; belowEMA200: number | null;
  deliveryPct: number;
  sectorBreadth: Array<{ sector: string; advPct: number }>;
}

function renderWithData(container: HTMLElement, d: BreadthData): void {
  const total = d.advancers + d.decliners + d.unchanged;
  const advPct = total > 0 ? (d.advancers / total * 100).toFixed(0) : '0';
  const decPct = total > 0 ? (d.decliners / total * 100).toFixed(0) : '0';
  const breadthColor = d.advancers > d.decliners ? 'var(--india-green)' : 'var(--india-red)';

  const sectorBars = d.sectorBreadth.map(s => `
    <div class="breadth-sector">
      <span class="breadth-sector-name">${s.sector}</span>
      <div class="breadth-bar-track">
        <div class="breadth-bar-adv" style="width:${s.advPct}%"></div>
        <div class="breadth-bar-dec" style="width:${100 - s.advPct}%"></div>
      </div>
      <span class="breadth-sector-pct">${s.advPct}%</span>
    </div>`).join('') || '<div class="wm-empty">Sector breadth unavailable</div>';

  container.innerHTML = `
    <div class="market-breadth-viz glass-card">
      <div class="card-header">
        <h3>📊 Market Breadth</h3>
      </div>
      <div class="breadth-main">
        <div class="breadth-circle">
          <div class="circle-stat" style="color:${breadthColor}">
            <span class="circle-value">${advPct}%</span>
            <span class="circle-label">Advancing</span>
          </div>
        </div>
        <div class="breadth-counts">
          <div class="breadth-count up">🟢 ${d.advancers} Advancers</div>
          <div class="breadth-count down">🔴 ${d.decliners} Decliners</div>
          <div class="breadth-count flat">⚪ ${d.unchanged} Unchanged</div>
        </div>
      </div>
      <div class="breadth-bar-full">
        <div class="bar-adv" style="width:${advPct}%"></div>
        <div class="bar-unch" style="width:${total > 0 ? (d.unchanged / total * 100).toFixed(0) : 0}%"></div>
        <div class="bar-dec" style="width:${decPct}%"></div>
      </div>
      <div class="breadth-extra">
        <div class="breadth-stat"><span>52w Highs</span><span class="up">${d.high52w}</span></div>
        <div class="breadth-stat"><span>52w Lows</span><span class="down">${d.low52w}</span></div>
        <div class="breadth-stat"><span>Above 200 EMA</span><span>${d.aboveEMA200 ?? 'N/A'}</span></div>
        <div class="breadth-stat"><span>Delivery %</span><span>${d.deliveryPct}%</span></div>
      </div>
      <div class="breadth-sectors">
        <h4>Sector Breadth</h4>
        ${sectorBars}
      </div>
    </div>`;
}

export function renderMarketBreadthViz(container: HTMLElement, data?: BreadthData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = renderUnavailableBlock('market-breadth-viz glass-card', 'Loading market breadth...');
    fetchBreadth().then(apiData => {
      if (apiData) {
        const mapped: BreadthData = {
          advancers: apiData.advances || 0,
          decliners: apiData.declines || 0,
          unchanged: apiData.unchanged || 0,
          high52w: apiData.new52wHigh || 0,
          low52w: apiData.new52wLow || 0,
          aboveEMA200: null,
          belowEMA200: null,
          deliveryPct: apiData.deliveryPercent || 0,
          sectorBreadth: [],
        };
        renderWithData(container, mapped);
      } else {
        container.innerHTML = renderUnavailableBlock('market-breadth-viz glass-card', 'Live market breadth unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('market-breadth-viz glass-card', 'Live market breadth unavailable');
    });
  }
}
